import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { PageSettingsProvider, usePageSettings } from './context/PageSettingsContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import Home from './pages/Home';
import Services from './pages/Services';
import AISolutions from './pages/AISolutions';
import Staffing from './pages/Staffing';
import Industries from './pages/Industries';
import CaseStudies from './pages/CaseStudies';
import About from './pages/About';
import Blog from './pages/Blog';
import BlogArticle from './pages/BlogArticle';
import Careers from './pages/Careers';
import Contact from './pages/Contact';
import Admin from './pages/Admin';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import JobApply from './pages/JobApply';
import Redirect from './pages/Redirect';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import VisionPlatform from './pages/VisionPlatform';

const PageDisabled = () => (
  <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#0a0a0a', color: '#fff', textAlign: 'center', padding: '2rem' }}>
    <h2 style={{ fontSize: '2rem', marginBottom: '1rem' }}>🚧 Page Unavailable</h2>
    <p style={{ color: '#888' }}>This page has been temporarily disabled by the admin.</p>
  </div>
);

function PageRoute({ pageId, element }) {
  const { isEnabled, loading } = usePageSettings();
  if (loading) return null;
  return isEnabled(pageId) ? element : <PageDisabled />;
}

function AppRoutes() {
  return (
    <>
      <ScrollToTop />
      <Navbar />
      <Routes>
        <Route path="/" element={<PageRoute pageId="home" element={<Home />} />} />
        <Route path="/services" element={<PageRoute pageId="services" element={<Services />} />} />
        <Route path="/ai-solutions" element={<PageRoute pageId="ai-solutions" element={<AISolutions />} />} />
        <Route path="/staffing" element={<PageRoute pageId="staffing" element={<Staffing />} />} />
        <Route path="/industries" element={<PageRoute pageId="industries" element={<Industries />} />} />
        <Route path="/case-studies" element={<PageRoute pageId="case-studies" element={<CaseStudies />} />} />
        <Route path="/about" element={<PageRoute pageId="about" element={<About />} />} />
        <Route path="/blog" element={<PageRoute pageId="blog" element={<Blog />} />} />
        <Route path="/blog/:slug" element={<PageRoute pageId="blog" element={<BlogArticle />} />} />
        <Route path="/careers" element={<PageRoute pageId="careers" element={<Careers />} />} />
        <Route path="/contact" element={<PageRoute pageId="contact" element={<Contact />} />} />
        <Route path="/privacy" element={<PageRoute pageId="privacy" element={<Privacy />} />} />
        <Route path="/terms" element={<PageRoute pageId="terms" element={<Terms />} />} />
        <Route path="/job/:jobId" element={<JobApply />} />
        <Route path="/go" element={<Redirect />} />
        <Route path="/products/vision-platform" element={<VisionPlatform />} />
        <Route path="/login" element={<Login />} />
        <Route path="/admin" element={
          <ProtectedRoute allowedRoles={['admin']}><Admin /></ProtectedRoute>
        } />
        <Route path="/dashboard/internal" element={
          <ProtectedRoute allowedRoles={['internal_user']}><Dashboard /></ProtectedRoute>
        } />
        <Route path="/dashboard/external" element={
          <ProtectedRoute allowedRoles={['external_user']}><Dashboard /></ProtectedRoute>
        } />
        <Route path="/unauthorized" element={
          <div style={{ color: '#fff', textAlign: 'center', padding: '6rem 2rem' }}>
            <h2>Access Denied</h2>
            <p style={{ color: '#888' }}>You don't have permission to view this page.</p>
          </div>
        } />
        <Route path="*" element={
          <div style={{ minHeight: '80vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'var(--color-bg)', color: '#fff', textAlign: 'center', padding: '2rem' }}>
            <p style={{ fontSize: '6rem', fontWeight: 800, background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', lineHeight: 1, marginBottom: '1rem' }}>404</p>
            <h2 style={{ fontSize: '1.75rem', marginBottom: '0.75rem' }}>Page Not Found</h2>
            <p style={{ color: '#64748b', marginBottom: '2rem', maxWidth: 380 }}>The page you're looking for doesn't exist or has been moved.</p>
            <a href="/" style={{ padding: '0.75rem 2rem', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', borderRadius: 8, fontWeight: 600, textDecoration: 'none' }}>Back to Home</a>
          </div>
        } />
      </Routes>
      <Footer />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <PageSettingsProvider>
        <Router>
          <AppRoutes />
        </Router>
      </PageSettingsProvider>
    </AuthProvider>
  );
}

export default App;
